import { useEffect, useRef, useCallback } from "react";
import type { LayerInspectorSelection } from "../../../../helpers/layerInspectorTools";
import type { ElementBounds } from "../types";
import {
  getElementBoundsFromElement,
  getElementByClassName,
  getVideoContainer,
} from "../../../../helpers/elementBoundsTracker";

interface UseElementBoundsOptions {
  selection: LayerInspectorSelection | undefined;
  enabled: boolean;
  onBoundsChange: (bounds: ElementBounds | null) => void;
  isGestureActive: boolean;
}

/**
 * Hook to track the bounds of a selected element in the video.
 * Uses requestAnimationFrame to keep bounds updated during animations.
 */
export function useElementBounds({
  selection,
  enabled,
  onBoundsChange,
  isGestureActive,
}: UseElementBoundsOptions): void {
  const frameRef = useRef<number | null>(null);
  const lastBoundsRef = useRef<ElementBounds | null>(null);
  const lastSelectionRef = useRef<string | null>(null);
  const elementRef = useRef<HTMLElement | SVGGraphicsElement | null>(null);

  const boundsEqual = useCallback(
    (
      nextBounds: ElementBounds | null,
      prevBounds: ElementBounds | null,
      tolerance = 0.25
    ) => {
      if (!nextBounds || !prevBounds) return nextBounds === prevBounds;
      const closeEnough = (a: number, b: number) =>
        Math.abs(a - b) <= tolerance;
      return (
        closeEnough(nextBounds.x, prevBounds.x) &&
        closeEnough(nextBounds.y, prevBounds.y) &&
        closeEnough(nextBounds.width, prevBounds.width) &&
        closeEnough(nextBounds.height, prevBounds.height) &&
        closeEnough(nextBounds.rotation, prevBounds.rotation) &&
        closeEnough(nextBounds.centerX, prevBounds.centerX) &&
        closeEnough(nextBounds.centerY, prevBounds.centerY)
      );
    },
    []
  );

  const updateBounds = useCallback(() => {
    if (!enabled || !selection?.className) {
      if (lastBoundsRef.current !== null) {
        lastBoundsRef.current = null;
        onBoundsChange(null);
      }
      return;
    }

    const container = getVideoContainer();
    if (!container) {
      if (lastBoundsRef.current !== null) {
        lastBoundsRef.current = null;
        onBoundsChange(null);
      }
      return;
    }

    if (lastSelectionRef.current !== selection.className) {
      lastSelectionRef.current = selection.className;
      elementRef.current = null;
    }

    let element = elementRef.current;
    if (!element || !element.isConnected) {
      element = getElementByClassName(selection.className, container);
      elementRef.current = element;
    }

    const bounds = element
      ? getElementBoundsFromElement(element, container)
      : null;

    // Only update if bounds actually changed (to prevent unnecessary re-renders)
    if (!boundsEqual(bounds, lastBoundsRef.current)) {
      lastBoundsRef.current = bounds;
      onBoundsChange(bounds);
    }
  }, [selection, enabled, onBoundsChange, boundsEqual]);

  useEffect(() => {
    if (!enabled || !selection) {
      // Clean up and notify
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      lastBoundsRef.current = null;
      lastSelectionRef.current = null;
      elementRef.current = null;
      onBoundsChange(null);
      return;
    }

    // Don't poll DOM bounds during an active gesture — component uses frozen initialBounds
    if (isGestureActive) {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      return;
    }

    // Start animation frame loop
    const loop = () => {
      updateBounds();
      frameRef.current = requestAnimationFrame(loop);
    };

    frameRef.current = requestAnimationFrame(loop);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [selection, enabled, isGestureActive, updateBounds, onBoundsChange]);
}
